import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Armchair, Download, ImageUp, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { apiErrorMessage, useDemoSitesApi } from '../../../common/hooks/use-demo-sites-api.hook';
import { ModelPreview } from './model-preview';

/** How often a model being made is asked about, and for how long before giving up. */
export const FURNITURE_POLL_MS = 5000;
const FURNITURE_TIMEOUT_MS = 10 * 60 * 1000;

const glbName = (photo: File | null): string =>
  `${(photo?.name || 'furniture').replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'furniture'}.glb`;

type Stage = { kind: 'idle' } | { kind: 'making'; since: number } | { kind: 'done' } | { kind: 'failed'; error: string };

/**
 * The 3D generator's furniture: a photo of the piece in, its textured 3D model
 * (.glb) out, made by fal (image to 3D) through the API. It takes a minute or
 * two: the job is started, then polled until the model can be fetched.
 */
export const FurnitureGenerator: React.FC<{ pollMs?: number }> = ({ pollMs = FURNITURE_POLL_MS }) => {
  const { t } = useTranslation();
  const api = useDemoSitesApi();

  const [photo, setPhoto] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [model, setModel] = useState<{ url: string; name: string; bytes: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  // The job being followed; a new run (or leaving the page) stops following the old one.
  const run = useRef(0);

  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);
  useEffect(() => () => { if (model) URL.revokeObjectURL(model.url); }, [model]);
  useEffect(() => () => { run.current += 1; }, []);
  useEffect(() => {
    if (stage.kind !== 'making') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [stage.kind]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    multiple: false,
    disabled: stage.kind === 'making',
    onDrop: (files) => {
      if (!files[0]) return;
      setPhoto(files[0]);
      setModel(null);
      setStage({ kind: 'idle' });
    },
  });

  const make = async () => {
    if (!photo) return;
    const mine = ++run.current;
    const since = Date.now();
    setModel(null);
    setNow(since);
    setStage({ kind: 'making', since });
    const fail = (error: string) => { if (run.current === mine) setStage({ kind: 'failed', error }); };
    try {
      const { jobId } = await api.startFurnitureModel(photo);
      while (run.current === mine) {
        await new Promise((r) => setTimeout(r, pollMs));
        if (run.current !== mine) return;
        const status = await api.furnitureModelStatus(jobId);
        if (status.status === 'failed') return fail(status.error || t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.FAILED'));
        if (status.status === 'completed') break;
        if (Date.now() - since > FURNITURE_TIMEOUT_MS) return fail(t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.TIMEOUT'));
      }
      if (run.current !== mine) return;
      const glb = await api.furnitureModelGlb(jobId);
      if (run.current !== mine) return;
      const url = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
      setModel({ url, name: glbName(photo), bytes: glb.byteLength });
      setStage({ kind: 'done' });
    } catch (e) {
      fail((await apiErrorMessage(e)) || t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.FAILED'));
    }
  };

  const making = stage.kind === 'making';
  const elapsed = making ? Math.max(0, Math.round((now - stage.since) / 1000)) : 0;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      <Card className="flex flex-col gap-6 p-6">
        <Field>
          <FieldLabel>{t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.PHOTO')}</FieldLabel>
          {photo && photoUrl ? (
            <div className="relative overflow-hidden rounded-lg border bg-muted">
              <img src={photoUrl} alt={photo.name} className="mx-auto block max-h-72 object-contain" />
              {!making && (
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute end-2 top-2 size-8 bg-background"
                  aria-label={t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.REMOVE_PHOTO')}
                  onClick={() => { setPhoto(null); setModel(null); setStage({ kind: 'idle' }); }}
                >
                  <X />
                </Button>
              )}
            </div>
          ) : (
            <div
              {...getRootProps()}
              data-testid="furniture-photo-drop"
              className={cn(
                'flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground transition-colors',
                isDragActive && 'border-foreground bg-muted',
              )}
            >
              <input {...getInputProps()} data-testid="furniture-photo-input" />
              <ImageUp className="size-6" />
              <span>{t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.DROP')}</span>
            </div>
          )}
          <FieldDescription>{t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.PHOTO_HINT')}</FieldDescription>
        </Field>

        {stage.kind === 'failed' && <p className="text-sm text-destructive" role="alert">{stage.error}</p>}

        <Button size="lg" onClick={make} disabled={!photo || making}>
          {making ? <Loader2 data-icon="inline-start" className="animate-spin" /> : model ? <RefreshCw data-icon="inline-start" /> : <Sparkles data-icon="inline-start" />}
          {making
            ? t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.MAKING', { seconds: elapsed })
            : t(model ? 'ADMIN_TOOLS.MODEL_GENERATOR.FURN.REMAKE' : 'ADMIN_TOOLS.MODEL_GENERATOR.FURN.MAKE')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('ADMIN_TOOLS.MODEL_GENERATOR.FURN.COST_HINT')}</p>
      </Card>

      <Card className="flex min-h-[420px] flex-col overflow-hidden p-0">
        {model ? (
          <>
            <div className="min-h-[360px] flex-1 bg-muted/40">
              <ModelPreview src={model.url} alt={model.name} testId="furniture-preview" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
              <div className="text-sm">
                <div className="font-medium">{model.name}</div>
                <div className="text-xs text-muted-foreground">{t('SUPER_ADMIN.RUG_GENERATOR.MODEL_SIZE', { kb: Math.round(model.bytes / 1024) })}</div>
              </div>
              <Button asChild>
                <a href={model.url} download={model.name} data-testid="furniture-download">
                  <Download data-icon="inline-start" />
                  {t('SUPER_ADMIN.RUG_GENERATOR.DOWNLOAD')}
                </a>
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
            {making ? <Loader2 className="size-8 animate-spin" /> : <Armchair className="size-8" />}
            <span>{t(making ? 'ADMIN_TOOLS.MODEL_GENERATOR.FURN.WAIT' : 'ADMIN_TOOLS.MODEL_GENERATOR.FURN.EMPTY')}</span>
          </div>
        )}
      </Card>
    </div>
  );
};
